import { lazy, Suspense } from 'react';
import { z } from 'zod';
import { Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import type { TileDefinition } from './types';

/**
 * Lazy import — Recharts (~120KB gzipped) is the heaviest dependency
 * in the dashboard. Splitting it into a separate chunk keeps the
 * non-dashboard authenticated bundle slim. Vite emits the import as
 * its own JS file; the browser fetches it only when this tile mounts.
 */
const ThroughputTrendTile = lazy(() =>
  import('../throughput-trend-tile').then((m) => ({ default: m.ThroughputTrendTile }))
);

export const throughputTrendTile: TileDefinition = {
  type: 'throughput_trend',
  title: 'Throughput trend',
  description: 'Daily tasks created vs completed — flow, not stocks.',
  icon: Activity,
  metrics: ['throughput_trend'],
  // Half-width by default; pair side-by-side with another tile or
  // resize to full width via Edit mode if a wider read is desired.
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  configSchema: z.object({}).strict(),
  defaultConfig: {},
  render: ({ period, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'throughput_trend',
      from: period.from,
      to: period.to,
      enabled,
    });
    return (
      <Suspense
        fallback={
          <div className="flex h-full min-h-0 flex-col gap-2">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="min-h-0 flex-1" />
          </div>
        }
      >
        <ThroughputTrendTile data={metric?.items} isLoading={isLoading} isError={isError} />
      </Suspense>
    );
  },
};
